import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowLeft, CheckCircle2, AlertTriangle, ExternalLink, Banknote, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const StatusBadge = ({ ok, label }) => (
  <Badge className={ok ? "bg-[#4A5D23] text-white" : "bg-[#E0E0E0] text-[#666]"}>
    {ok ? <CheckCircle2 className="w-3 h-3 mr-1"/> : <AlertTriangle className="w-3 h-3 mr-1"/>}
    {label}
  </Badge>
);

const Payouts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [savingCcy, setSavingCcy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/seller/stripe/status");
      setStatus(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load Stripe status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  // Phase 2A — let the seller pick the Stripe Connect settlement currency.
  const updatePayoutCurrency = async (code) => {
    setSavingCcy(true);
    try {
      const { data } = await api.patch("/seller/stripe/payout-currency", { currency: code });
      setStatus((s) => (s ? { ...s, payout_currency: data.payout_currency } : s));
      toast.success(`Settlement currency updated to ${code.toUpperCase()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update settlement currency");
    } finally {
      setSavingCcy(false);
    }
  };

  // Handle OAuth round-trip flag
  useEffect(() => {
    const flag = params.get("stripe");
    if (flag === "connected") {
      toast.success("Stripe account connected — payouts will route to you directly.");
      setParams({}, { replace: true });
    } else if (flag === "error") {
      toast.error(`Stripe didn't finish: ${params.get("reason") || "unknown"}`);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/seller/stripe/connect-link");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Stripe connection");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect your Stripe account from AfriMarket?")) return;
    setDisconnecting(true);
    try {
      await api.post("/seller/stripe/disconnect");
      toast.success("Disconnected from Stripe");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Disconnect failed");
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return <div className="max-w-3xl mx-auto py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#C05A3C]"/></div>;
  }

  const ready = status?.connected && status?.charges_enabled && status?.payouts_enabled;
  const pending = status?.connected && !ready;

  return (
    <div className="max-w-3xl mx-auto py-10 px-4 sm:px-6">
      <button onClick={() => navigate("/dashboard")} className="text-sm text-muted-foreground hover:text-[#C05A3C] flex items-center gap-1 mb-6" data-testid="payouts-back">
        <ArrowLeft className="w-4 h-4"/> Back to dashboard
      </button>

      <div className="mb-2 label-overline text-[#C05A3C]">Payouts</div>
      <h1 className="font-display text-4xl mb-2" data-testid="payouts-h1">Get paid directly to your bank</h1>
      <p className="text-sm text-muted-foreground mb-8 max-w-xl">
        Connect your Stripe account once. Every sale you make on AfriMarket goes straight to your bank — minus the 8% platform fee — no manual payouts, no waiting on us.
      </p>

      {/* Trust hero strip — only shown to sellers who haven't connected yet,
          where it has the highest activation impact. */}
      {!status?.connected && (
        <div
          className="relative overflow-hidden rounded-lg border border-[#635BFF]/20 bg-gradient-to-r from-[#635BFF]/8 via-[#635BFF]/4 to-transparent px-5 py-4 mb-6 flex items-center gap-4"
          data-testid="stripe-trust-strip"
        >
          <div className="shrink-0 w-10 h-10 rounded-md bg-[#635BFF] flex items-center justify-center text-white font-bold shadow-sm">S</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#1A2024]">
              Already trusted by 4M+ sellers worldwide
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Stripe handles payments for Amazon, Shopify, Lyft &amp; millions of independent sellers — your AfriMarket payouts ride the same rails.
            </div>
          </div>
        </div>
      )}

      {/* Status card */}
      <div className="bg-white border border-black/10 rounded-lg p-6 mb-6" data-testid="stripe-status-card">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md bg-[#635BFF] flex items-center justify-center text-white font-bold">S</div>
            <div>
              <div className="font-display text-lg">Stripe Connect</div>
              <div className="text-xs text-muted-foreground">Standard onboarding · 135+ countries</div>
            </div>
          </div>
          {ready && <Badge className="bg-[#4A5D23] text-white" data-testid="stripe-ready-badge"><CheckCircle2 className="w-3 h-3 mr-1"/> Ready for payouts</Badge>}
          {pending && <Badge className="bg-[#D9923B] text-[#1A2024]" data-testid="stripe-pending-badge"><AlertTriangle className="w-3 h-3 mr-1"/> Onboarding incomplete</Badge>}
          {!status?.connected && <Badge variant="outline" data-testid="stripe-not-connected-badge">Not connected</Badge>}
        </div>

        {status?.connected ? (
          <>
            <div className="grid sm:grid-cols-3 gap-2 mb-5">
              <StatusBadge ok={status.charges_enabled} label="Charges enabled"/>
              <StatusBadge ok={status.payouts_enabled} label="Payouts enabled"/>
              <StatusBadge ok={status.details_submitted} label="Details submitted"/>
            </div>
            <div className="text-xs text-muted-foreground space-y-1 mb-5 font-sans-ui">
              {status.account_id && <div><span className="font-semibold">Account:</span> {status.account_id}</div>}
              {status.country && <div><span className="font-semibold">Country:</span> {status.country.toUpperCase()}</div>}
              {status.default_currency && <div><span className="font-semibold">Stripe default:</span> {status.default_currency.toUpperCase()}</div>}
              {status.requirements_due?.length > 0 && (
                <div className="text-[#C05A3C] mt-2">
                  <span className="font-semibold">Action needed in Stripe:</span> {status.requirements_due.slice(0, 3).join(", ")}
                </div>
              )}
            </div>

            {/* Phase 2A settlement currency picker */}
            {status.allowed_payout_currencies?.length > 0 && (
              <div className="bg-[#F3EFE9] border border-black/10 rounded-md p-4 mb-5" data-testid="settlement-currency-card">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <div className="text-xs label-overline text-[#C05A3C]">Settlement currency</div>
                    <div className="text-sm text-[#1A2024] font-medium">
                      Stripe will pay you in <span className="font-display text-base" data-testid="settlement-currency-value">{(status.payout_currency || "usd").toUpperCase()}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      Platform FX fee: <strong>{((status.platform_fx_fee_bps || 0) / 100).toFixed(2)}%</strong> · disclosed transparently to buyers at checkout
                    </div>
                  </div>
                  <select
                    className="bg-white border border-black/10 rounded-md px-3 py-2 text-sm font-medium uppercase focus:outline-none focus:ring-2 focus:ring-[#C05A3C]/30"
                    value={(status.payout_currency || "usd").toLowerCase()}
                    onChange={(e) => updatePayoutCurrency(e.target.value)}
                    disabled={savingCcy}
                    data-testid="settlement-currency-select"
                  >
                    {status.allowed_payout_currencies.map((c) => (
                      <option key={c} value={c}>{c.toUpperCase()}</option>
                    ))}
                  </select>
                </div>
                <div
                  className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-[#1A2024]/70 bg-white/70 border border-black/10 rounded-full px-2.5 py-1"
                  data-testid="powered-by-stripe-badge"
                  title="Funds split automatically at checkout — your share is wired to your Stripe account, AfriMarket keeps the platform fee."
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#635BFF]" aria-hidden="true" />
                  Powered by Stripe — instant payouts
                </div>
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => window.open("https://dashboard.stripe.com/", "_blank")} data-testid="open-stripe-dashboard">
                <ExternalLink className="w-4 h-4 mr-1"/> Open Stripe dashboard
              </Button>
              <Button variant="outline" onClick={refresh} data-testid="refresh-stripe-status">Refresh status</Button>
              <Button variant="outline" onClick={disconnect} disabled={disconnecting} className="text-[#C05A3C] border-[#C05A3C]/40 hover:bg-[#C05A3C]/5" data-testid="stripe-disconnect-btn">
                {disconnecting && <Loader2 className="w-4 h-4 mr-1 animate-spin"/>}
                Disconnect
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm font-sans-ui mb-5">
              Click below to be redirected to Stripe, sign in (or create an account), and authorise AfriMarket to send payments to you. Takes about 2 minutes.
            </p>
            {!status?.configured && (
              <div className="bg-[#D9923B]/10 border border-[#D9923B]/30 rounded-md p-3 mb-4 flex gap-2 items-start text-xs" data-testid="stripe-config-warning">
                <ShieldAlert className="w-4 h-4 text-[#D9923B] flex-shrink-0 mt-0.5"/>
                <div>
                  <div className="font-semibold text-[#1A2024]">Setup pending on platform side</div>
                  <div className="text-muted-foreground">The AfriMarket admin still needs to add the Stripe Connect Client ID to the platform&apos;s environment. You can click below — we&apos;ll show you a friendly error so the admin knows what to do.</div>
                </div>
              </div>
            )}
            <Button onClick={connect} disabled={connecting} className="bg-[#635BFF] hover:bg-[#5048E5] text-white" data-testid="stripe-connect-btn">
              {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Banknote className="w-4 h-4 mr-2"/>}
              Connect with Stripe
            </Button>
          </>
        )}
      </div>

      {/* Trust + commission disclosure */}
      <div className="bg-[#F3EFE9] border border-black/10 rounded-lg p-5 text-xs text-muted-foreground font-sans-ui">
        <div className="font-semibold text-[#1A2024] mb-1">How it works</div>
        <ul className="space-y-1 list-disc list-inside">
          <li>Buyers pay through AfriMarket. Stripe routes the funds to your account.</li>
          <li>AfriMarket takes an <strong>8% platform fee</strong> (10% for services). Stripe deducts its standard processing fee.</li>
          <li>You see all payouts inside your own Stripe Dashboard. We never touch your bank details.</li>
          <li>Buyers paying in a different currency are charged in theirs — Stripe converts to yours automatically.</li>
          <li>A transparent <strong>0.5% platform FX fee</strong> is applied when currencies differ — disclosed to buyers, never hidden.</li>
        </ul>
      </div>
    </div>
  );
};

export default Payouts;
